import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_CLIENTS, MOCK_PARTICIPANTS, MOCK_TRANSPORT } from '@/mocks/directories'
import { USE_MOCKS } from '@/mocks/fixtures'
import type {
  Client,
  DirectoryContact,
  DirectoryParticipant,
  ParticipantRole,
  TransportRef,
} from '@/types/domain'

/* The directory — Epic 7.
 *
 * Real endpoints over real tables. Two things the client deliberately does NOT
 * do, because the server already does them and doing them twice is how the two
 * copies drift:
 *
 *   - it does not filter rows by role. A Sales user's list arrives already
 *     scoped (AD-5), and agent-typed participants are simply absent rather
 *     than present-and-hidden (FR-29).
 *   - it does not decide who may merge or delete. The button is hidden from
 *     roles that cannot (an affordance), and the server refuses regardless
 *     (the enforcement).
 */

/** The body a client form submits. Contacts are sent whole: the form owns the
 *  list, so a partial update would make "delete this contact" ambiguous. */
export interface ClientPayload {
  name: string
  code: string
  taxId: string
  registrationId: string
  country: string
  address: string
  contacts: ContactPayload[]
}

export interface ContactPayload {
  label: string
  email: string
  phone: string
  wechat: string
  telegram: string
  whatsapp: string
  isPrimary: boolean
}

export interface ParticipantPayload extends Omit<ClientPayload, 'code'> {
  type: ParticipantRole
}

/* ── clients (Story 7.1) ──────────────────────────────────────────────────── */

export function useClients(
  search?: string,
  /** For callers that only need the directory conditionally — global search,
   *  which must not pull it on a page load that never opens the box. */
  { enabled }: { enabled?: boolean } = {},
) {
  return useQuery<Client[]>({
    queryKey: queryKeys.directories.clients({ search }),
    enabled,
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_CLIENTS
        : (await api.get<{ items: Client[] }>('/directories/clients', {
            params: { search },
          })).items,
  })
}

/** One client with its contacts.
 *
 * Contacts arrive only on the detail read — a directory of two hundred clients
 * does not need six hundred contact rows to render a list. `ListClients` in the
 * directory service loads none, so `contacts` on a row from `useClients` is
 * always `[]`, never "this client has no contacts".
 *
 * The mock branch reads the same fixture the list does rather than staying
 * disabled forever, which is what it used to do: every caller of this hook
 * wants contacts, and a mock session that answered the detail read with
 * permanent `isPending` gave the surfaces below an empty contact list — the
 * exact bug this hook exists to avoid on the real path. */
export function useClient(id: string | undefined) {
  return useQuery<Client>({
    queryKey: queryKeys.directories.client(id ?? ''),
    queryFn: async () => {
      if (!USE_MOCKS) return api.get<Client>(`/directories/clients/${id}`)
      const mock = MOCK_CLIENTS.find((client) => client.id === id)
      // Thrown rather than returned as undefined: react-query rejects an
      // undefined result with a message about the query, and a fixture gap
      // should say which id was missing.
      if (!mock) throw new Error(`no mock client for id ${id}`)
      return mock
    },
    enabled: Boolean(id),
  })
}

/** The contacts on one client, for the surfaces that pick one (FR-4).
 *
 * A hook rather than three copies of `useClient(id).data?.contacts ?? []`,
 * because the copies are what went wrong: the quote surfaces read `contacts`
 * off the LIST instead, and the list carries none — so the contact picker was
 * permanently empty and the "Defaults to …" hint never named anybody.
 *
 * The directory's own ordering is preserved. It is load-bearing: the server's
 * `quote.firstAddressable` picks "the first one carrying an email, primary
 * first", so a re-sort here would make a hint name a different person from the
 * one the send actually addresses. */
export function useClientContacts(clientId: string | undefined): DirectoryContact[] {
  return useClient(clientId).data?.contacts ?? []
}

function useInvalidateDirectory() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.directories.all })
  }
}

export function useCreateClient() {
  const invalidate = useInvalidateDirectory()
  return useMutation<Client, Error, ClientPayload>({
    mutationFn: (payload) => api.post<Client>('/directories/clients', payload),
    onSuccess: invalidate,
  })
}

export function useUpdateClient() {
  const invalidate = useInvalidateDirectory()
  return useMutation<Client, Error, { id: string; payload: ClientPayload }>({
    mutationFn: ({ id, payload }) => api.put<Client>(`/directories/clients/${id}`, payload),
    onSuccess: invalidate,
  })
}

export function useSetClientActive() {
  const invalidate = useInvalidateDirectory()
  return useMutation<Client, Error, { id: string; isActive: boolean }>({
    mutationFn: ({ id, isActive }) =>
      api.patch<Client>(`/directories/clients/${id}/active`, { isActive }),
    onSuccess: invalidate,
  })
}

/** Deletes a client.
 *
 * Refused with 409 when quotes or cases reference it, and the message NAMES
 * them (FR-39) — so the error surfaces as-is rather than being replaced with a
 * generic one, because the names are the actionable part. */
export function useDeleteClient() {
  const invalidate = useInvalidateDirectory()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/directories/clients/${id}`),
    onSuccess: invalidate,
  })
}

/** Folds one client into another. The id in the path is the record being KEPT. */
export function useMergeClients() {
  const invalidate = useInvalidateDirectory()
  return useMutation<void, Error, { targetId: string; sourceId: string }>({
    mutationFn: ({ targetId, sourceId }) =>
      api.post<void>(`/directories/clients/${targetId}/merge`, { sourceId }),
    onSuccess: invalidate,
  })
}

/* ── participants (Story 7.2) ─────────────────────────────────────────────── */

/** Folds one participant into another. The id in the path is the record KEPT.
 *
 * `POST /directories/participants/:id/merge` has been mounted since Epic 7 and
 * had no hook, so the action FR-39 offers when a delete is refused existed on
 * the server and nowhere else. Same shape as useMergeClients deliberately: the
 * two refusals are one feature and a reader comparing them should find one
 * pattern.
 *
 * The server additionally refuses a merge across TYPES (a shipper folded into an
 * agent would move a shipment's counterparty into the hidden partition), which
 * arrives as a validation problem on `type` rather than as a reference refusal. */
export function useMergeParticipants() {
  const invalidate = useInvalidateDirectory()
  return useMutation<void, Error, { targetId: string; sourceId: string }>({
    mutationFn: ({ targetId, sourceId }) =>
      api.post<void>(`/directories/participants/${targetId}/merge`, { sourceId }),
    onSuccess: invalidate,
  })
}

export function useParticipants(type?: ParticipantRole, search?: string) {
  return useQuery<DirectoryParticipant[]>({
    queryKey: queryKeys.directories.participants({ type, search }),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_PARTICIPANTS
        : (await api.get<{ items: DirectoryParticipant[] }>('/directories/participants', {
            params: { type, search },
          })).items,
  })
}

export function useCreateParticipant() {
  const invalidate = useInvalidateDirectory()
  return useMutation<DirectoryParticipant, Error, ParticipantPayload>({
    mutationFn: (payload) =>
      api.post<DirectoryParticipant>('/directories/participants', payload),
    onSuccess: invalidate,
  })
}

export function useUpdateParticipant() {
  const invalidate = useInvalidateDirectory()
  return useMutation<
    DirectoryParticipant,
    Error,
    { id: string; payload: ParticipantPayload }
  >({
    mutationFn: ({ id, payload }) =>
      api.put<DirectoryParticipant>(`/directories/participants/${id}`, payload),
    onSuccess: invalidate,
  })
}

export function useSetParticipantActive() {
  const invalidate = useInvalidateDirectory()
  return useMutation<DirectoryParticipant, Error, { id: string; isActive: boolean }>({
    mutationFn: ({ id, isActive }) =>
      api.patch<DirectoryParticipant>(`/directories/participants/${id}/active`, { isActive }),
    onSuccess: invalidate,
  })
}

export function useDeleteParticipant() {
  const invalidate = useInvalidateDirectory()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/directories/participants/${id}`),
    onSuccess: invalidate,
  })
}

/* ── transport reference data (Story 7.4) ─────────────────────────────────── */

/** Reads one or both lists.
 *
 * `activeOnly` is what a CHOOSER passes: a deactivated entry no longer appears
 * when choosing transport on a new case, while the maintenance screen asks for
 * everything so it can offer reactivation (FR-43). */
export function useTransportRefs(kind?: TransportRef['kind'], activeOnly = false) {
  return useQuery<TransportRef[]>({
    queryKey: queryKeys.directories.transport({ kind, activeOnly }),
    queryFn: async () =>
      USE_MOCKS
        ? MOCK_TRANSPORT
        : (await api.get<{ items: TransportRef[] }>('/directories/transport', {
            params: { kind, activeOnly: activeOnly || undefined },
          })).items,
  })
}

export interface TransportPayload {
  kind: TransportRef['kind']
  label: string
  code: string
  sortOrder: number
}

export function useCreateTransportRef() {
  const invalidate = useInvalidateDirectory()
  return useMutation<TransportRef, Error, TransportPayload>({
    mutationFn: (payload) => api.post<TransportRef>('/directories/transport', payload),
    onSuccess: invalidate,
  })
}

export function useUpdateTransportRef() {
  const invalidate = useInvalidateDirectory()
  return useMutation<TransportRef, Error, { id: string; payload: TransportPayload }>({
    mutationFn: ({ id, payload }) =>
      api.put<TransportRef>(`/directories/transport/${id}`, payload),
    onSuccess: invalidate,
  })
}

/** Deactivates an entry rather than deleting it — the action offered when a
 *  delete is refused (FR-43). */
export function useSetTransportRefActive() {
  const invalidate = useInvalidateDirectory()
  return useMutation<TransportRef, Error, { id: string; isActive: boolean }>({
    mutationFn: ({ id, isActive }) =>
      api.patch<TransportRef>(`/directories/transport/${id}/active`, { isActive }),
    onSuccess: invalidate,
  })
}

export function useDeleteTransportRef() {
  const invalidate = useInvalidateDirectory()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.delete<void>(`/directories/transport/${id}`),
    onSuccess: invalidate,
  })
}

/* ── helpers ──────────────────────────────────────────────────────────────── */

/** An empty contact row, for the "add a contact" button. */
export function emptyContact(): ContactPayload {
  return {
    label: '',
    email: '',
    phone: '',
    wechat: '',
    telegram: '',
    whatsapp: '',
    isPrimary: false,
  }
}

/** Turns a stored contact back into a form row.
 *
 * A SUPPRESSED field becomes an empty string, never the marker: a user who may
 * not read a phone number must not be able to save the marker object back over
 * the real one. The form disables those inputs, and this is the second guard —
 * if the input were ever re-enabled, the value still would not round-trip. */
export function contactToPayload(contact: DirectoryContact): ContactPayload {
  const plain = (value: DirectoryContact['email']) =>
    typeof value === 'string' ? value : ''

  return {
    label: contact.label,
    email: plain(contact.email),
    phone: plain(contact.phone),
    wechat: plain(contact.wechat),
    telegram: plain(contact.telegram),
    whatsapp: plain(contact.whatsapp),
    isPrimary: contact.isPrimary,
  }
}
